import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import appRoutes from '../../../../routing/app-routes';
import {
  clearInsertFiscalCodeLegalPersonError,
  resetInsertFiscalCodeLegalPersonRequestState,
  setInsertFiscalCodeLegalPerson,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { insertFiscalCodeLegalPerson } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { messagesForInsertFiscalCodeLegalPersonError } from '../../../../store/features/user-access/user-registration/user-registration-utils';

import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import { useAppSelector } from '../../../redux';
import { env } from '../../../../env';

export interface InsertFiscalCodePageLogicCallbacks {
  buttonDisabled: boolean;
  userCode: string;
  userBehalfOfCode:string;
  userCodeUnchanged:string;
  vatCodeFiscalCode: string;
  fiscalCodeLegalPerson: string;
  insertFiscalCodeLegalPersonRequestStatus: ApiRequestStatus;
  updateFiscalCodeLegalPerson: (value?: string) => void;
  handleSubmit: () => void;
}

export const useInsertFiscalCodePageLogic = (
  actions: GlobalActions
): InsertFiscalCodePageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    userCode,
    vatCodeFiscalCode,
    fiscalCodeLegalPerson,
    insertFiscalCodeLegalPersonRequestStatus,
    insertFiscalCodeLegalPersonError,
    userBehalfOfCode,
    userCodeUnchanged
  } = useAppSelector((state) => state.features.userRegistration);

  const [buttonDisabled, setButtonDisabled] = useState(
    fiscalCodeLegalPerson?.length === 0
  );

  const updateFiscalCodeLegalPerson = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setInsertFiscalCodeLegalPerson(value));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    dispatch(insertFiscalCodeLegalPerson());
  }, [dispatch]);

  useEffect(() => {
    if (insertFiscalCodeLegalPersonRequestStatus === 'succeeded') {
      dispatch(resetInsertFiscalCodeLegalPersonRequestState());
      navigate(appRoutes.WelcomeUserRegistration, { replace: false });
    }
  }, [dispatch, navigate, insertFiscalCodeLegalPersonRequestStatus]);

  useEffect(() => {
    if (insertFiscalCodeLegalPersonError) {
      const errorMessages = messagesForInsertFiscalCodeLegalPersonError(
        insertFiscalCodeLegalPersonError
      );
      const link =
        insertFiscalCodeLegalPersonError === 'UTENZA_NON_MIGRATA'
          ? env.oldMyAzimutUrl
          : undefined;
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title, {
          a: (...chunks) =>
            `<a href=${link} target="_self" rel="noopener noreferrer">${chunks}</a>`,
        }),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearInsertFiscalCodeLegalPersonError());
    }
  }, [dispatch, formatMessage, insertFiscalCodeLegalPersonError]);

  return {
    buttonDisabled,
    userCode,
    userBehalfOfCode,
    userCodeUnchanged,
    vatCodeFiscalCode,
    fiscalCodeLegalPerson,
    insertFiscalCodeLegalPersonRequestStatus,
    updateFiscalCodeLegalPerson,
    handleSubmit,
  };
};
